import React, { useEffect } from 'react';

import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { useMediaQuery } from '@pkg/providers';

import { useLockBodyScroll } from '@app/helpers';

import { Typography } from '../DesignSystem';

import {
    Background,
    FullScreenWrapper,
    ImageHeader,
    ImageHeaderContainer,
    WelcomeButton,
    WelcomeContent,
    WelcomeModalWrapper,
    WelcomeTitle,
} from './styles';

interface WelcomeModalProps {
    onClose(): void;
}

const customEase = [0.33, 1, 0.68, 1];

const WelcomeModal = ({ onClose }: WelcomeModalProps) => {
    const { t: translate } = useTranslation('onboarding');
    useLockBodyScroll(true);

    useEffect(() => {
        function handleKeyDown(event) {
            if (event.key === 'Escape') {
                onClose();
            }
        }

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);

    const { mobile } = useMediaQuery();

    return (
        <AnimatePresence mode="wait">
            <FullScreenWrapper>
                <Background
                    onClick={onClose}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.75, transition: { ease: customEase, duration: 0.4, delay: 0.0 } }}
                    exit={{ opacity: 0, transition: { duration: 0.5 } }}
                />
                <WelcomeModalWrapper
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { ease: customEase, duration: 0.3, delay: 0.4 } }}
                    exit={{ opacity: 0, transition: { duration: 0.5 } }}
                >
                    <ImageHeaderContainer>
                        <ImageHeader
                            initial={{ scale: 1.2 }}
                            animate={{ scale: 1, transition: { ease: customEase, duration: 0.7, delay: 0.4 } }}
                        />
                    </ImageHeaderContainer>
                    <WelcomeTitle
                        initial={{ opacity: 0, y: 20 }}
                        animate={{
                            opacity: 1,
                            y: 0,
                            transition: { ease: customEase, duration: 0.7, delay: 0.45 },
                        }}
                    >
                        <Typography size={mobile ? 36 : 30} type="headline" variant="span" align="center" weight="700">
                            {translate('welcome_title')}
                        </Typography>
                    </WelcomeTitle>

                    <WelcomeContent
                        initial={{ opacity: 0, y: 20 }}
                        animate={{
                            opacity: 1,
                            y: 0,
                            transition: { ease: customEase, duration: 0.7, delay: 0.5 },
                        }}
                    >
                        <Typography
                            size={mobile ? 18 : 16}
                            type="label"
                            variant="span"
                            align="center"
                            weight="400"
                            color="#c4c4c4"
                        >
                            {translate('welcome_description_start')}
                            <strong>{translate('welcome_description_strong')}</strong>
                            {translate('welcome_description_end')}
                        </Typography>
                    </WelcomeContent>
                    <WelcomeButton
                        initial={{ opacity: 0, y: 10 }}
                        animate={{
                            opacity: 1,
                            y: 0,
                            transition: { ease: customEase, duration: 0.7, delay: 0.7 },
                        }}
                    >
                        <Button
                            id="explore-pods-onboarding-button"
                            size={mobile ? 'medium' : 'medium'}
                            fluid={!mobile}
                            onClick={onClose}
                        >
                            {translate('welcome_button')}
                        </Button>
                    </WelcomeButton>
                </WelcomeModalWrapper>
            </FullScreenWrapper>
        </AnimatePresence>
    );
};

export default WelcomeModal;
